import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { AnalysisDisplayOption, AnalysisOption, Pipeline } from '../../types/domain';
import { SelectOption } from '../../components/common/Dropdown';
import { createPipeline, getPipelineAnalysisOptions } from '../../api/pipelineMaintenance/api';
import { showSuccessToast, showErrorToast } from '../../hooks/use-toast';
import { Label } from '../../components/common/FormLabel';
import { ErrorLabel } from '../../components/common/ErrorLabel';
import { Input } from '../../components/common/Input';
import { TextareaField } from '../../components/common/FormFields';
import { FormSingleColumn } from '../../components/specific/pipeline/FormSingleColumn';
import { FormHeader } from '../../components/specific/pipeline/FormHeader';
import { PipelineAnalysisOptions } from '../../components/specific/pipeline/PipelineAnalysisOptions';
import { PipelineStagesTable } from '../../components/specific/pipeline/PipelineStagesTable';
import { SubmitButton } from '../../components/common/Button/SubmitButton';
import { useNavigate } from 'react-router';
import { ButtonCancel } from '../../components/common/Button';
import { LoadingOverlay } from '../../components/common/Loading/LoadingOverlay';
import { useLayout } from '../../contexts/LayoutContext';
import { validateNoHtml } from '../../utils/fieldValidation';

const PipelineMaintenanceCreatePipelinePage = () => {
  const navigate = useNavigate();
  const { data: layoutData } = useLayout();

  const formMethods = useForm<Pipeline>();

  const [analysisOptions, setAnalysisOptions] = useState<SelectOption[]>([]); // available options for dropdown
  const [analysisDisplayOptions, setAnalysisDisplayOptions] = useState<AnalysisDisplayOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const erpConfigured = layoutData.erpConfigured;
  const [quotationEnabled, setQuotationEnabled] = useState(false);

  useEffect(() => {

    const fetchPipelineAnalysisOptions = async () => {
      try {
        const options = await getPipelineAnalysisOptions();
          setAnalysisOptions(options.map((option: AnalysisOption): SelectOption => {
            return {
              value: option.index,
              label: option.fullName
            };
          }));

          setAnalysisDisplayOptions(options.map((option: AnalysisOption): AnalysisDisplayOption => {
            return {
              index: option.index,
              isMandatory: false,
              isReadonly: false,
              shortName: option.shortName,
              fullName: option.fullName
            };
          }));
      } catch (error) {
      }
    };

    fetchPipelineAnalysisOptions();
  }, []);

  /**
   * Handles the form submission.
   * @param {Pipeline} data - The form data.
   */
  const onSubmit: SubmitHandler<Pipeline> = async (data) => {
    setSubmitting(true);
    try {
      await createPipeline(data);
      showSuccessToast("Pipeline created successfully.");
      navigate('/PipelineMaintenance');
    } catch (error) {
      showErrorToast("Failed to create pipeline. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuotationEnabledChange = (isChecked: boolean) => {
    formMethods.setValue('quotationEnabled', isChecked);
    setQuotationEnabled(isChecked);

    if (!isChecked) {
      // If quotation enabled is turned off, we need to clear any quotation stage or won stage designations from the stages
      const stages = formMethods.getValues('stages') || [];
      stages.forEach((stage, index) => {
        if (stage?.quotationStage || stage?.wonStage) {
          formMethods.setValue(`stages.${index}.quotationStage`, false);
          formMethods.setValue(`stages.${index}.wonStage`, false);
        }
      });
    }
  }

  return (
    <>
      <LoadingOverlay 
        isVisible={submitting} 
        message="Creating pipeline..." 
      />
      <FormProvider {...formMethods}>
        <FormSingleColumn onSubmit={formMethods.handleSubmit(onSubmit)}>
          <FormHeader title="Pipeline" formInformation="Create a new pipeline. This is where you can describe your sales process and configure pipeline analysis options.">
            <SubmitButton text={'Create Pipeline +'} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold"/>
            <ButtonCancel text='Cancel' className='text-kPurple-500' />
          </FormHeader>
          <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-5">
              <Label forField='name' label={Translation.PipelineMaintenance.lblName} />
              <ErrorLabel error={formMethods.formState.errors.name?.message} />
              <Input
                inErrorState={!!formMethods.formState.errors.name}
                placeholder="Enter a name for your pipeline"
                {...formMethods.register('name', { 
                  required: 'required', 
                  maxLength: { value: 50, message: 'max 50 characters' },
                  validate: validateNoHtml
                })}
              />
            </div>
            <div className="md:col-span-5">
              <TextareaField
                label="Description"
                labelFor='description'
                error={formMethods.formState.errors.description?.message}
                inErrorState={!!formMethods.formState.errors.description}
                placeholder="Enter a description for your pipeline"
                {...formMethods.register('description', { required: 'required', maxLength: { value: 200, message: 'max 200 characters' }, validate: validateNoHtml })}
              />
            </div>

            {erpConfigured &&
            <div className="md:col-span-5">
              <div className="text-gray-600">
                <p className="font-semibold text-lg py-2">ERP Service Options</p>
                <p>Configure this pipeline to enable ERP service integration.</p>
                <p>Once configured, pipeline stages can be configured to define which pipeline stage is designated as the quotation stage, and which pipeline stage is designated as the won stage.</p>
                <hr className="pb-4 mt-2" />
              </div>
            </div>}

            {erpConfigured &&
            <div className="md:col-span-5 mt-1">
              <Label forField="quotationEnabled" label={"Quotations Enabled"} />
              <p className="font-normal text-gray-600 py-1">
                Enable quotations for this pipeline.</p>
              <input
                  {...formMethods.register("quotationEnabled", {
                        onChange: (e) => handleQuotationEnabledChange(e.target.checked)
                      })}
                  className="size-5 rounded-sm border-gray-300 mt-2"
                  type="checkbox" />
            </div>
            }

            <div className="md:col-span-5">
              <div className="text-gray-600">
                <p className="font-semibold text-lg py-2">Analysis Display Options</p>
                <p>Which Analysis fields would you like to make available to your pipeline and how should they be configured?</p>
                <p>Selecting 'ReadOnly' or 'Mandatory' is optional.</p>
                <hr className="pb-4 mt-2" />
              </div>
            </div>

            <PipelineAnalysisOptions
              analysisOptions={analysisOptions}
              analysisDisplayOptions={analysisDisplayOptions}
              setValue={formMethods.setValue}
              getValues={formMethods.getValues}
            />

            <PipelineStagesTable quotationEnabled={quotationEnabled} />

            {/* this should really be a form footer where you can add multiple buttons */}
            <div className="md:col-span-5 text-right mt-4">
              <div className="inline-flex gap-2 w-1/2 flex-row-reverse">
                <SubmitButton text={'Create Pipeline +'} className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold" />
                <ButtonCancel text='Cancel' className='text-kPurple-500'/>
              </div>
            </div>
          </div>
        </FormSingleColumn>
      </FormProvider>
    </>
  );
};

export default PipelineMaintenanceCreatePipelinePage;
